'use client'

import { useState, useEffect, useCallback, useMemo } from 'react'
import { apiFetch } from '@/lib/api'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Textarea } from '@/components/ui/textarea'
import { toast } from 'sonner'
import { Loader2, Clock, Coffee, LogOut, CheckCircle2, Calendar, CalendarX, Pencil } from 'lucide-react'
import { AttendanceOverrideModal } from './AttendanceOverrideModal'
import { formatTime, cn } from '@/lib/utils'
import { todayIST } from '@/lib/date-utils'
import { GlassCard } from '@/components/ui/glass-card'
import { StatusPill, statusTone } from '@/components/ui/status-pill'
import { TabLoader, EmptyState } from '@/components/ui/loading'
import { PRIMARY_BTN } from '@/components/ui/button-styles'

interface BreakEntry {
  start: string | null
  end: string | null
}

interface AttendanceRecord {
  date: string
  checkIn: string | null
  checkOut: string | null
  status: string
  workMode: string
  notes: string | null
  breaks?: BreakEntry[]
}

interface AdminSelfAttendanceProps {
  userId: string
  /** When false, renders only the compact today card without the history list. */
  showHistory?: boolean
}

function totalBreakMinutes(breaks?: BreakEntry[]): number {
  if (!Array.isArray(breaks)) return 0
  let t = 0
  for (const b of breaks) {
    if (b.start && b.end) {
      const [sh, sm] = b.start.split(':').map(Number)
      const [eh, em] = b.end.split(':').map(Number)
      const d = (eh * 60 + em) - (sh * 60 + sm)
      if (d > 0) t += d
    }
  }
  return t
}

export function AdminSelfAttendance({ userId, showHistory = true }: AdminSelfAttendanceProps) {
  const [today, setToday] = useState<AttendanceRecord | null>(null)
  const [history, setHistory] = useState<AttendanceRecord[]>([])
  const [limit, setLimit] = useState(10)
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [workMode, setWorkMode] = useState('OFFICE')
  const [confirmCheckout, setConfirmCheckout] = useState(false)
  const [workDescription, setWorkDescription] = useState('')
  // Row the user is correcting via the override modal. This component only ever
  // renders inside the admin shell, which is ADMIN/HR-only, and the backend
  // re-checks the role on POST /api/attendance/override — so self-service here
  // does not widen who can edit attendance.
  const [overrideTarget, setOverrideTarget] = useState<AttendanceRecord | null>(null)

  const todayStr = todayIST()
  // Bounded window instead of the user's entire history. The unscoped read is
  // what made this card fail under Firestore read-quota pressure, and a failed
  // fetch left `today` null — so the card offered "Check In" to someone already
  // checked in and the break buttons never appeared.
  const rangeStart = useMemo(() => {
    const d = new Date(`${todayStr}T00:00:00`)
    d.setDate(d.getDate() - 89)
    return d.toISOString().slice(0, 10)
  }, [todayStr])

  const fetchToday = useCallback(async () => {
    if (!userId) return
    try {
      const res = await apiFetch(`/api/attendance?userId=${userId}&startDate=${rangeStart}&endDate=${todayStr}`)
      const data = await res.json()
      const records: AttendanceRecord[] = (data.attendance || []) as AttendanceRecord[]
      setToday(records.find(r => r.date === todayStr) || null)
      setHistory(records)
    } catch (e) {
      console.error(e)
      toast.error(e instanceof Error ? e.message : 'Could not load your attendance')
    } finally {
      setLoading(false)
    }
  }, [userId, todayStr, rangeStart])

  useEffect(() => {
    fetchToday()
  }, [fetchToday])

  async function postAction(action: string, body: any = {}) {
    setBusy(true)
    try {
      const res = await apiFetch('/api/attendance', {
        method: 'POST',
        body: JSON.stringify({ action, ...body }),
      })
      return await res.json()
    } finally {
      setBusy(false)
    }
  }

  const handleCheckIn = async () => {
    try {
      await postAction('checkin', { workMode })
      toast.success('Checked in')
      await fetchToday()
    } catch (e: any) { toast.error(e.message) }
  }

  // Break responses carry the updated day, so flip the button from the response
  // rather than waiting on a refetch that may be slow or fail — that lag is what
  // made Start/End Break look like it did nothing.
  const applyBreakResponse = (data: any) => {
    if (!Array.isArray(data?.breaks)) return
    setToday(prev => (prev ? { ...prev, breaks: data.breaks } : prev))
  }

  const handleBreakStart = async () => {
    try {
      applyBreakResponse(await postAction('breakstart'))
      toast.success('Break started')
      await fetchToday()
    } catch (e: any) { toast.error(e.message) }
  }

  const handleBreakEnd = async () => {
    try {
      applyBreakResponse(await postAction('breakend'))
      toast.success('Break ended')
      await fetchToday()
    } catch (e: any) { toast.error(e.message) }
  }

  const handleCheckOut = async () => {
    if (!workDescription.trim()) {
      toast.error('Please enter a work description')
      return
    }
    setConfirmCheckout(false)
    try {
      await postAction('checkout', { notes: workDescription })
      toast.success('Checked out')
      setWorkDescription('')
      await fetchToday()
    } catch (e: any) { toast.error(e.message) }
  }

  const breaks = today?.breaks || []
  const openBreak = breaks.find(b => b.start && !b.end)
  const completedBreakCount = breaks.filter(b => b.start && b.end).length
  const isCheckedIn = !!today?.checkIn
  const isCheckedOut = !!today?.checkOut


  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr + 'T00:00:00')
    const day = String(d.getDate()).padStart(2, '0')
    const month = String(d.getMonth() + 1).padStart(2, '0')
    const year = d.getFullYear()
    return `${day}/${month}/${year}`
  }

  return (
    <div className="space-y-5 md:space-y-6">
      <GlassCard tone={isCheckedOut ? 'green' : isCheckedIn ? 'orange' : 'amber'}>
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="space-y-2">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-sm font-semibold tracking-tight text-neutral-900 dark:text-white/90">My Attendance</h3>
              <StatusPill tone={statusTone(isCheckedIn ? 'PRESENT' : 'ABSENT')}>
                {isCheckedIn ? (isCheckedOut ? 'COMPLETED' : 'PRESENT') : 'NOT CHECKED IN'}
              </StatusPill>
              {isCheckedIn && <StatusPill tone="blue">{today?.workMode || workMode}</StatusPill>}
            </div>
            <div className="grid grid-cols-3 gap-4 max-w-sm">
              <div>
                <p className="text-[10px] uppercase tracking-wider text-neutral-400 dark:text-white/40">Check In</p>
                <p className="text-base font-semibold text-neutral-900 dark:text-white/90 tabular-nums">{formatTime(today?.checkIn ?? null) || '—'}</p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wider text-neutral-400 dark:text-white/40">Check Out</p>
                <p className="text-base font-semibold text-neutral-900 dark:text-white/90 tabular-nums">{formatTime(today?.checkOut ?? null) || '—'}</p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wider text-neutral-400 dark:text-white/40">Breaks</p>
                <p className="text-base font-semibold text-neutral-900 dark:text-white/90 tabular-nums">{breaks.filter(b => b.start && b.end).length}</p>
              </div>
            </div>
          </div>

          <div className="w-full md:w-64 space-y-2">
            {loading ? (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="h-5 w-5 animate-spin text-orange-400" />
              </div>
            ) : !isCheckedIn ? (
              <>
                <Select value={workMode} onValueChange={setWorkMode}>
                  <SelectTrigger className="border-neutral-200 dark:border-white/10"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="OFFICE">Office</SelectItem>
                    <SelectItem value="WFH">Work From Home</SelectItem>
                    <SelectItem value="HYBRID">Hybrid</SelectItem>
                  </SelectContent>
                </Select>
                <Button
                  onClick={handleCheckIn}
                  disabled={busy}
                  className={cn('w-full h-11', PRIMARY_BTN)}
                >
                  {busy ? <Loader2 className="mr-2 h-5 w-5 animate-spin" /> : <Clock className="mr-2 h-5 w-5" />}
                  Check In Now
                </Button>
              </>
            ) : isCheckedOut ? (
              <div className="flex items-center justify-center gap-2 rounded-xl bg-emerald-500/10 ring-1 ring-inset ring-emerald-500/20 px-4 py-4 text-emerald-600 dark:text-emerald-300">
                <CheckCircle2 className="h-5 w-5" />
                <span className="font-medium">Day completed</span>
              </div>
            ) : openBreak ? (
              <Button
                onClick={handleBreakEnd}
                disabled={busy}
                className="w-full h-11 bg-gradient-to-br from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-white shadow-lg shadow-amber-500/20"
              >
                {busy ? <Loader2 className="mr-2 h-5 w-5 animate-spin" /> : <Coffee className="mr-2 h-5 w-5" />}
                End Break
              </Button>
            ) : (
              <div className="space-y-2">
                <Button onClick={handleBreakStart} disabled={busy} variant="outline" className="w-full border-neutral-200 dark:border-white/15 bg-neutral-50 dark:bg-white/5 text-neutral-900 dark:text-white/90 hover:bg-neutral-100 dark:hover:bg-white/10">
                  {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Coffee className="mr-2 h-4 w-4" />}
                  Start Break
                </Button>
                <Button
                  onClick={() => setConfirmCheckout(true)}
                  disabled={busy}
                  className={cn('w-full h-11', PRIMARY_BTN)}
                >
                  <LogOut className="mr-2 h-4 w-4" />
                  Check Out
                </Button>
              </div>
            )}
          </div>
        </div>
      </GlassCard>

      {/* History */}
      {showHistory && (
        <GlassCard>
          <div className="flex items-center justify-between mb-3">
            <div>
              <h3 className="text-sm font-semibold tracking-tight text-neutral-900 dark:text-white/90">My Recent Attendance</h3>
              {/* Was hardcoded "Last 10 days", which went stale as soon as
                  Show More raised the limit. */}
              <p className="text-[11px] text-neutral-400 dark:text-white/40">
                Showing {Math.min(limit, history.length)} of {history.length} day{history.length === 1 ? '' : 's'} · you can correct any entry
              </p>
            </div>
          </div>
          {loading ? (
            <TabLoader />
          ) : history.length === 0 ? (
            <EmptyState
              icon={CalendarX}
              title="No attendance records yet"
              description="Your check-ins will appear here once you start marking attendance."
            />
          ) : (
            <div className="space-y-2.5">
              {history.slice(0, limit).map(record => {
                const status = record.checkIn ? 'PRESENT' : 'ABSENT'
                const bm = record.breaks ? totalBreakMinutes(record.breaks) : 0
                return (
                  <div
                    key={record.date}
                    className="rounded-xl p-3 bg-neutral-50/50 dark:bg-white/[0.02] ring-1 ring-inset ring-neutral-200 dark:ring-white/5 hover:ring-neutral-300 dark:ring-white/15 transition-colors"
                  >
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                      <div className="flex items-center gap-2.5">
                        <Calendar className="h-4 w-4 text-neutral-400 dark:text-white/40" />
                        <span className="text-sm text-neutral-800 dark:text-white/80 tabular-nums">{formatDate(record.date)}</span>
                        <StatusPill tone={statusTone(status)}>{status}</StatusPill>
                        <Button
                          size="sm"
                          variant="ghost"
                          aria-label={`Edit attendance for ${formatDate(record.date)}`}
                          className="h-7 px-2 text-orange-600 hover:text-orange-700 hover:bg-orange-500/10 dark:text-orange-300 dark:hover:text-orange-200"
                          onClick={() => setOverrideTarget(record)}
                        >
                          <Pencil className="h-3.5 w-3.5 mr-1" /> Edit
                        </Button>
                      </div>
                      {status === 'PRESENT' && (
                        <div className="space-y-1 mt-1 sm:mt-0 text-right">
                          <div className="flex items-center gap-2 flex-wrap text-[11px] text-neutral-500 dark:text-white/50 justify-end">
                            <span>In <span className="text-neutral-800 dark:text-white/80 tabular-nums">{formatTime(record.checkIn)}</span></span>
                            <span>Out <span className="text-neutral-800 dark:text-white/80 tabular-nums">{formatTime(record.checkOut) || '—'}</span></span>
                            {bm > 0 && <StatusPill tone="amber">Break {bm}m</StatusPill>}
                            <StatusPill tone="blue">{record.workMode}</StatusPill>
                          </div>
                          {record.notes && (
                            <div className="text-xs text-neutral-600 dark:text-white/60 bg-neutral-100/50 dark:bg-white/[0.01] rounded-lg p-2 mt-1 text-left">
                              <span className="font-medium text-neutral-700 dark:text-white/70">Work Done: </span>{record.notes}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )
              })}
              {history.length > limit && (
                <div className="flex justify-center pt-2">
                  <Button variant="ghost" className="text-orange-500 hover:text-orange-600 text-xs font-semibold" onClick={() => setLimit(prev => prev + 10)}>
                    Show More
                  </Button>
                </div>
              )}
            </div>
          )}
        </GlassCard>
      )}

      {overrideTarget && (
        <AttendanceOverrideModal
          open={true}
          onOpenChange={(o) => !o && setOverrideTarget(null)}
          userId={userId}
          date={overrideTarget.date}
          currentCheckIn={overrideTarget.checkIn}
          currentCheckOut={overrideTarget.checkOut}
          currentBreaks={overrideTarget.breaks || []}
          onSaved={() => { setOverrideTarget(null); fetchToday() }}
        />
      )}

      <AlertDialog open={confirmCheckout} onOpenChange={setConfirmCheckout}>
        <AlertDialogContent className="bg-white/95 dark:bg-neutral-950/95 backdrop-blur-2xl border-neutral-200 dark:border-white/10 text-neutral-900 dark:text-neutral-100">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-neutral-900 dark:text-white/90">
              Check out for the day?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-neutral-500 dark:text-white/60">
              Your check-out time will be recorded as the current time. You won&apos;t be able to check back in today.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <label className="text-sm font-medium text-neutral-700 dark:text-white/80">Work Description (Required)</label>
              <Textarea
                value={workDescription}
                onChange={e => setWorkDescription(e.target.value)}
                placeholder="Describe the work you completed today..."
                className="bg-transparent border-neutral-200 dark:border-white/10"
              />
            </div>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-neutral-200 dark:border-white/10">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleCheckOut} disabled={busy} className={PRIMARY_BTN}>
              Confirm Check Out
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
